import React, { useState, useCallback, useRef, useEffect } from 'react';
import ImageEditor from 'tui-image-editor';
import 'tui-image-editor/dist/tui-image-editor.css';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Loader2 } from 'lucide-react';
import { apiClient } from '@/services/api';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

interface ImageEditorModalProps {
  isOpen: boolean;
  onClose: () => void;
  imageSrc: string;
  onSave: (newImageUrl: string) => void;
}

interface EditorImage {
  url: string;
  release: () => void;
}

const loadImageElement = (src: string): Promise<HTMLImageElement> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Image failed to decode'));
    img.src = src;
  });

// SVGs without width/height have no intrinsic size, which leaves the editor canvas empty.
// Draw them to a PNG first, sized from the SVG itself, its viewBox, or a fallback.
const rasterizeSvg = async (svgBlob: Blob): Promise<string> => {
  const text = await svgBlob.text();
  const objectUrl = URL.createObjectURL(new Blob([text], { type: 'image/svg+xml' }));
  try {
    const img = await loadImageElement(objectUrl);
    let width = img.naturalWidth;
    let height = img.naturalHeight;
    if (!width || !height) {
      const viewBox = text.match(/viewBox\s*=\s*["']\s*[-\d.]+[\s,]+[-\d.]+[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
      width = viewBox ? parseFloat(viewBox[1]) : 1024;
      height = viewBox ? parseFloat(viewBox[2]) : 768;
    }
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width));
    canvas.height = Math.max(1, Math.round(height));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas unavailable');
    context.drawImage(img, 0, 0, canvas.width, canvas.height);
    return canvas.toDataURL('image/png');
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
};

// The editor reads pixels back from its canvas, so the image must load with CORS. Our storage
// (S3 and the CDN) only sends CORS headers when the request has an Origin header and does not
// send Vary: Origin, so the copy the article's <img> already cached has no CORS headers and the
// editor's CORS request fails against it. Fetching with cache: 'no-store' skips that cached copy,
// and handing the editor a local blob/data URL keeps its canvas untainted.
const prepareEditorImage = async (src: string, signal: AbortSignal): Promise<EditorImage> => {
  if (src.startsWith('data:') || src.startsWith('blob:')) {
    return { url: src, release: () => {} };
  }

  const response = await fetch(src, { mode: 'cors', cache: 'no-store', signal });
  if (!response.ok) {
    throw new Error(`Image request failed with ${response.status}`);
  }
  const blob = await response.blob();

  const isSvg = blob.type.includes('svg') || /\.svg(?:[?#]|$)/i.test(src);
  if (isSvg) {
    return { url: await rasterizeSvg(blob), release: () => {} };
  }

  const objectUrl = URL.createObjectURL(blob);
  return { url: objectUrl, release: () => URL.revokeObjectURL(objectUrl) };
};

export const ImageEditorModal: React.FC<ImageEditorModalProps> = ({
  isOpen,
  onClose,
  imageSrc,
  onSave,
}) => {
  const { t } = useTranslation();
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const editorRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Initialize editor when modal opens
  useEffect(() => {
    if (!isOpen || !imageSrc) {
      return;
    }

    setLoadError(null);

    let timeoutId: NodeJS.Timeout | null = null;
    let isMounted = true;
    let editorImage: EditorImage | null = null;
    const abortController = new AbortController();

    // Start downloading right away, while the dialog and editor are being set up.
    const imagePromise = prepareEditorImage(imageSrc, abortController.signal);
    imagePromise.catch(() => {}); // handled below; avoids an unhandled rejection if we unmount first

    const showLoadError = () => {
      if (isMounted) {
        setLoadError(t('editor.imageEditorModal.failedLoadImage'));
      }
    };

    const initEditor = async () => {
      if (!isMounted) return;

      // Wait for the container to be available
      if (!containerRef.current) {
        timeoutId = setTimeout(initEditor, 50);
        return;
      }

      // Clean up existing editor if any
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }

      let editor: ImageEditor;
      try {
        editor = new ImageEditor(containerRef.current, {
          includeUI: {
            menu: ['crop', 'flip', 'rotate', 'draw', 'shape', 'icon', 'text'],
            initMenu: 'shape',
            menuBarPosition: 'bottom',
          },
          cssMaxWidth: 700,
          cssMaxHeight: 500,
          selectionStyle: {
            cornerSize: 20,
            rotatingPointOffset: 70,
          },
        });
        editorRef.current = editor;
      } catch {
        if (isMounted) setLoadError(t('editor.imageEditorModal.failedInitEditor'));
        return;
      }

      try {
        const prepared = await imagePromise;
        if (!isMounted || editorRef.current !== editor) {
          prepared.release();
          return;
        }
        editorImage = prepared;

        const result: any = await editor.loadImageFromURL(editorImage.url, 'Image');
        if (!isMounted || editorRef.current !== editor) return;

        // Resize editor to match image dimensions
        if (result && editor.ui) {
          editor.ui.resizeEditor({
            imageSize: {
              oldWidth: result.oldWidth || 0,
              oldHeight: result.oldHeight || 0,
              newWidth: result.newWidth || result.oldWidth || 0,
              newHeight: result.newHeight || result.oldHeight || 0,
            },
          });
        }
        // Activate menu to enable all controls
        if (editor.ui) {
          // Present at runtime but missing from tui-image-editor typings
          (editor.ui as unknown as { activeMenuEvent?: () => void }).activeMenuEvent?.();
        }
      } catch (error) {
        if ((error as Error)?.name !== 'AbortError') {
          console.error('Image editor could not load image', imageSrc, error);
        }
        showLoadError();
      }
    };

    timeoutId = setTimeout(initEditor, 100);

    // Cleanup on unmount
    return () => {
      isMounted = false;
      abortController.abort();
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (editorRef.current) {
        editorRef.current.destroy();
        editorRef.current = null;
      }
      editorImage?.release();
    };
  }, [isOpen, imageSrc, t]);

  const handleSave = useCallback(async () => {
    if (!editorRef.current) {
      toast.error(t('editor.imageEditorModal.editorNotReady'));
      return;
    }

    setIsSaving(true);
    try {
      // Get the edited image as base64
      const dataURL = editorRef.current.toDataURL();
      
      if (!dataURL) {
        toast.error(t('editor.imageEditorModal.failedExport'));
        setIsSaving(false);
        return;
      }

      // Extract MIME type and base64 data
      const [prefix, base64String] = dataURL.split(',');
      const mimeMatch = prefix.match(/:(.*?);/);
      const mime = mimeMatch ? mimeMatch[1] : 'image/png';
      
      // Convert base64 to blob
      const byteCharacters = atob(base64String);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: mime });

      // Create a File from the blob
      const file = new File([blob], 'edited-image.png', { type: 'image/png' });

      // Upload the edited image
      const response = await apiClient.uploadAsset(file);
      
      if (response.ok && response.asset) {
        toast.success(t('editor.imageEditorModal.uploadSuccess'));
        onSave(response.asset.url);
        onClose();
      } else {
        toast.error(response.error || t('editor.imageEditorModal.failedUpload'));
      }
    } catch {
      toast.error(t('editor.imageEditorModal.failedSave'));
    } finally {
      setIsSaving(false);
    }
  }, [onSave, onClose, t]);

  const handleClose = useCallback(() => {
    if (editorRef.current) {
      editorRef.current.destroy();
      editorRef.current = null;
    }
    onClose();
  }, [onClose]);

  return (
    <>
      <style>{`
        [data-radix-dialog-overlay][data-state="open"] {
          z-index: 9999 !important;
        }
        [data-radix-dialog-content][data-state="open"] {
          z-index: 10000 !important;
        }
        .tui-image-editor-header-buttons,
        .tui-image-editor-header-logo {
          display: none !important;
        }
      `}</style>
      <Dialog open={isOpen} onOpenChange={handleClose}>
        <DialogContent 
          className="max-w-[95vw] max-h-[95vh] w-full h-full p-0"
          style={{ zIndex: 10000 }}
        >
        <DialogHeader className="px-6 pt-6 pb-0">
          <DialogTitle>{t('editor.imageEditorModal.editImage')}</DialogTitle>
        </DialogHeader>
        <div className="relative flex-1">
          <div
            ref={containerRef}
            className="overflow-hidden"
            style={{ height: 'calc(95vh - 120px)', minHeight: '500px' }}
          />
          {loadError && (
            <div
              role="alert"
              className="absolute inset-0 z-10 flex items-center justify-center bg-background/95 p-6"
            >
              <p className="flex max-w-md items-start gap-2 text-sm text-destructive">
                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                <span>{loadError}</span>
              </p>
            </div>
          )}
        </div>
        <DialogFooter className="px-6 pb-6 pt-0">
          <Button variant="outline" onClick={handleClose} disabled={isSaving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isSaving || !!loadError}>
            {isSaving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('common.saving')}
              </>
            ) : (
              t('editor.imageEditorModal.ok')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    </>
  );
};
